// utils/stickyStack.js
// ─────────────────────────────────────────────────────────────────────────────
// The storefront "sticky stack": the fixed Navbar, and under it the sticky
// section strip (Breakfast, Chicken Pizza, ...). Three pages carried it
// (restaurant, retail, healthcare) and all three hardcoded the numbers: the
// strip stuck at 121px, the scroll margins at 140/220, the scroll-to-section
// offset at a percentage of the viewport. On a phone the Navbar is three rows
// deep, so the strip parked itself over the search field, and the moment the
// page got a reviews block below the menu there was enough to scroll for
// everyone to see it. Reported 2 Oct 2026.
//
// Both bars now publish their real height (hooks/usePublishedHeight) and
// everything below reads those two variables: the strip's `top`, the margin a
// section keeps clear when scrolled to, and the programmatic scroll itself.
// ─────────────────────────────────────────────────────────────────────────────
import { css } from "styled-components";

export const NAV_H_VAR = "--lusuku-nav-h";
export const SECTIONS_H_VAR = "--lusuku-sections-h";
const NAV_H_FALLBACK = 121;
const SECTIONS_H_FALLBACK = 56;
/* Breathing room between the strip and the heading that scrolls up under it. */
const GAP = 8;

/** The strip: pinned exactly under the Navbar, whatever height it rendered at. */
export const stickyUnderNav = css`
  position: sticky;
  top: var(${NAV_H_VAR}, ${NAV_H_FALLBACK}px);
`;

/** A section heading scrolled to keeps clear of both bars. */
export const sectionScrollMargin = css`
  scroll-margin-top: calc(
    var(${NAV_H_VAR}, ${NAV_H_FALLBACK}px) + var(${SECTIONS_H_VAR}, ${SECTIONS_H_FALLBACK}px) + ${GAP}px
  );
`;

/** The inner menu scroller fits exactly in what the two bars leave of the viewport. */
export const heightUnderStack = (extraPx = 0) => css`
  height: calc(
    100vh - var(${NAV_H_VAR}, ${NAV_H_FALLBACK}px) - var(${SECTIONS_H_VAR}, ${SECTIONS_H_FALLBACK}px) - ${extraPx}px
  );
`;

const readPx = (name, fallback) => {
  if (typeof document === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
};

/** Navbar + strip + gap, in px, as rendered right now. */
export const stickyStackOffset = () =>
  readPx(NAV_H_VAR, NAV_H_FALLBACK) + readPx(SECTIONS_H_VAR, SECTIONS_H_FALLBACK) + GAP;

const scrollsItself = (el) => {
  if (!el || el === window) return false;
  const { overflowY } = getComputedStyle(el);
  return (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight;
};

/**
 * Scroll a section's heading to just under the sticky stack.
 *
 * Two scrollers can be involved. Restaurant and retail menus keep their
 * sections in an inner container that scrolls by itself, sitting right under
 * the strip; healthcare lets the window scroll (its container is plain flow,
 * and `container.scrollTo` on it used to be a silent no-op). So:
 *
 *   inner container  the window is first brought to where the strip is
 *                    pinned (so the container's top meets the stack), then
 *                    the container scrolls the heading to its own top;
 *   window           one scroll, the heading landing under both bars.
 *
 * `pinStack: false` skips the window step: the on-load "open on the first
 * section" call uses it, because pinning the strip on load would scroll the
 * banner away before the shopper has seen it.
 *
 * Positions are read from the viewport, not `offsetTop`, so the answer is the
 * same whichever element happens to be the offset parent.
 */
export const scrollToSection = (
  sectionElement,
  container,
  { behavior = "smooth", pinStack = true } = {}
) => {
  if (!sectionElement) return;
  const stack = readPx(NAV_H_VAR, NAV_H_FALLBACK) + readPx(SECTIONS_H_VAR, SECTIONS_H_FALLBACK);
  const rect = sectionElement.getBoundingClientRect();
  if (scrollsItself(container)) {
    const cRect = container.getBoundingClientRect();
    if (pinStack && Math.abs(cRect.top - stack) > 1) {
      window.scrollTo({ top: Math.max(0, window.scrollY + cRect.top - stack), behavior });
    }
    container.scrollTo({
      top: Math.max(0, rect.top - cRect.top + container.scrollTop - GAP),
      behavior,
    });
    return;
  }
  window.scrollTo({ top: Math.max(0, rect.top + window.scrollY - stack - GAP), behavior });
};
